import type { IntlShape } from 'react-intl';

import { computeText } from '@/locales/utils';
import type { MenuCategory } from '@/services/innovorder/categories/categories.type';
import type { MenuProduct } from '@/services/innovorder/products/products.type';
import type { PromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';

export function getLinkTypeInfo(
    banner: PromotionalBanner,
    intl: IntlShape,
    products: MenuProduct[],
    categories: MenuCategory[],
): { badgeText: string; subtitle?: string } {
    if (banner.linkType === 'none') {
        return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
    }
    if (banner.linkType === 'product') {
        const product = banner.linkTargetId ? products.find((p) => p.productId === banner.linkTargetId) : null;
        if (product) {
            return {
                badgeText: computeText(intl, 'promotionalBanner.table.linkProduct'),
                subtitle: product.name,
            };
        }
        // Brand banners use linkTargetRef (SKU string) — resolve to product name
        if (banner.linkTargetRef) {
            const matchedProduct = products.find((p) => p.sku?.value === banner.linkTargetRef);
            if (matchedProduct) {
                return {
                    badgeText: computeText(intl, 'promotionalBanner.table.linkProduct'),
                    subtitle: matchedProduct.name,
                };
            }
            // SKU doesn't exist in this restaurant's menu
            return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
        }
        return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
    }
    if (banner.linkType === 'category') {
        const category = banner.linkTargetId ? categories.find((c) => c.categoryId === banner.linkTargetId) : null;
        if (category) {
            return {
                badgeText: computeText(intl, 'promotionalBanner.table.linkCategory'),
                subtitle: category.name,
            };
        }
        // Brand banners use linkTargetRef (category name) — resolve against restaurant menu
        if (banner.linkTargetRef) {
            const matchedCategory = categories.find((c) => c.name.trim() === banner.linkTargetRef?.trim());
            if (matchedCategory) {
                return {
                    badgeText: computeText(intl, 'promotionalBanner.table.linkCategory'),
                    subtitle: matchedCategory.name,
                };
            }
            // Category doesn't exist in this restaurant's menu
            return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
        }
        return { badgeText: computeText(intl, 'promotionalBanner.table.linkNone') };
    }
    return { badgeText: '' };
}
